import { v4 } from 'uuid';

import { createFilterReducer } from '../filter';
import {
  dashboardEventsFilterActions,
  dashboardEventsFilterActionsPrefix,
} from '../../constants/filter';
import {
  aggregationTypes,
  chartAxes,
  dateColumns,
  usageEvents,
  views,
} from '../../constants/event';
import { dateFields, getDefaultGroupColumn, isEventSpecificField } from '../../utils/event/common';
import { excludeEventSpecificClauses } from '../../utils/filter';
import { fromFilter } from '../../converters/filterConverter';
import { TEMPLATE_AGGREGATION_TYPE, unitsTypes } from '../../constants/dashboards';
import { PARAM_CSV } from '../../utils/csv';
import { sortingOptions, sortingOrder } from '../../constants/common';
import { getViews } from '../../containers/dashboards/cards/builder/revenue/revenueOptionsBarUtils';
import periodReducer, { initialPeriod } from './eventsPeriods';

const filterReducer = createFilterReducer(dashboardEventsFilterActionsPrefix);

export const alphabet = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';

const columnChartSortingInitialState = {
  axis: chartAxes.Y,
  order: sortingOrder.DESCENDING,
  sortingOptions: sortingOptions.INDEPEND,
};

export const eventOptions = {
  columnChartSorting: columnChartSortingInitialState,
  views: [],
  view: 'Chart',
  singleChartMode: true,
  showFormula: false,
  calcTotal: false,
  averageLine: false,
  medianLine: false,
  trendLine: false,
  cumulative: false,
  showSortingOptions: false,
  enteredFormula: null,
  legendName: '',
  adaptive: false,
  isFormulaOnly: false,
};

export const createNewEvent = (letterNumber) => ({
  id: v4(),
  uniqueChar: alphabet[letterNumber],
  name: null,
  filter: filterReducer(undefined, {}),
  aggregationColumn: null,
  aggregationType: aggregationTypes.COUNT,
  splitColumn: null,
  othersFilter: null,
  sql: '',
  error: '',
  periodsState: {
    periods: [initialPeriod],
    isShow: false,
  },
});

const getEventsFromTemplate = (template) =>
  template.queries.map((item, index) => {
    return {
      ...createNewEvent(index),
      name: item.query.eventName,
      aggregationColumn:
        item.query.aggregations[0].field === undefined ? null : item.query.aggregations[0].field,
      aggregationType: TEMPLATE_AGGREGATION_TYPE[item.query.aggregations[0].type],
      filter: fromFilter(item.query.filter, (field) =>
        template.filterExtractor(item.query.eventName, field)
      ),
      splitColumn: item.query.groupBy[1] === undefined ? null : item.query.groupBy[1],
    };
  });

const createDuplicateEvent = (duplicatedEvent) => ({
  ...duplicatedEvent,
  id: v4(),
  filter: {
    ...duplicatedEvent.filter,
    clauses: duplicatedEvent.filter.clauses.map((clause) => ({
      ...clause,
      id: v4(),
    })),
  },
});

const csvInitial = {
  [PARAM_CSV.HEADER]: true,

  [PARAM_CSV.GROUP_BY]: false,
  [PARAM_CSV.GROUP_BY_DAY]: true,
  [PARAM_CSV.GROUP_BY_MS]: false,

  [PARAM_CSV.SPLIT_BY]: false,
  [PARAM_CSV.SPLIT_BY_DAY]: false,
  [PARAM_CSV.SPLIT_BY_MS]: false,

  [PARAM_CSV.COUNT]: true,

  [PARAM_CSV.SUM]: false,
  [PARAM_CSV.AVG_SUM]: false,

  [PARAM_CSV.MAX]: false,
  [PARAM_CSV.MIN]: false,
  [PARAM_CSV.AVG]: false,
};

export const initialEventState = {
  events: [],
  groupColumn: dateColumns.DAY,
  idUsingTemplate: undefined,
  csv: csvInitial,
  options: eventOptions,
};

const isEmptyOrSpecificField = (field) => !field || isEventSpecificField(field);

const isSplitAggregationType = (aggregationType) => {
  return (
    aggregationType === aggregationTypes.AVG_COUNT_SPLIT ||
    aggregationType === aggregationTypes.AVG_TOTAL_SPLIT
  );
};

const getEventAggregationType = (aggregationType, splitColumn) => {
  if (!isSplitAggregationType(aggregationType)) {
    return aggregationType;
  }
  if (isEmptyOrSpecificField(splitColumn)) {
    return aggregationTypes.COUNT;
  }
  return aggregationType;
};

const idPredicateBuilder = (id) => (e) => e.id === id;

const updateEvents = (collection, predicate, updateCallback) =>
  collection.map((item) => (predicate(item) ? updateCallback(item) : item));

const updateEventById = (collection, id, updateCallback) =>
  updateEvents(collection, idPredicateBuilder(id), updateCallback);

const moveCharLeft = (events, excludeId) => {
  let isMoveUniqueChar = false;
  return events.map((event) => {
    const eventVar = event;

    if (eventVar.id === excludeId) {
      isMoveUniqueChar = true;
    }
    if (isMoveUniqueChar) {
      const index = alphabet.indexOf(eventVar.uniqueChar);
      eventVar.uniqueChar = alphabet[index - 1];
    }
    return eventVar;
  });
};

const moveCharRight = (events, excludeIndex) => {
  const eventsVar = events;

  let isMoveUniqueChar = false;
  return eventsVar.map((event, i) => {
    const eventVar = event;

    if (i === excludeIndex) {
      isMoveUniqueChar = true;
    }
    if (isMoveUniqueChar) {
      eventVar.uniqueChar = alphabet[i];
    }
    return eventVar;
  });
};

const updateGroupColumn = (groupColumn, events) => {
  const eventNames = events.map((e) => e.name);
  const uniqEventNames = Array.from(new Set(eventNames));

  const selectedUsageEvents = uniqEventNames.filter((name) => !!usageEvents[name]);

  if (
    groupColumn &&
    isEventSpecificField(groupColumn) &&
    (uniqEventNames.length > 1 || eventNames.length === 1) &&
    !selectedUsageEvents[0]
  ) {
    return null;
  }

  return selectedUsageEvents.length > 0
    ? getDefaultGroupColumn(selectedUsageEvents[0])
    : groupColumn;
};

export const getView = (currentView, groupBy) => {
  const eventViews = dateFields.includes(groupBy)
    ? [views.CHART, views.STACKED_CHART, views.PERCENTAGE_CHART, views.TABLE]
    : [views.COLUMN_CHART, views.TABLE, views.PIE_CHART];
  return eventViews.includes(currentView) ? currentView : eventViews[0];
};

const eventsCompose = (state = initialEventState, action) => {
  switch (action.type) {
    case 'CREATE_INITIAL_EVENT': {
      return {
        ...state,
        events: [createNewEvent(0)],
      };
    }

    case 'DASHBOARD_EVENTS_COMPOSE_ADD_EVENT':
      return state.events.length < alphabet.length
        ? {
            ...state,
            events: [...state.events, createNewEvent(state.events.length)],
          }
        : state;

    case 'DASHBOARD_EVENTS_COMPOSE_DUPLICATE_EVENT': {
      const eventsArr = [...state.events];
      const indexOfDuplicatedEvent = eventsArr
        .map((event) => event.id)
        .reduce((prevValue, id, index) => {
          const prevValueVar = prevValue;
          prevValueVar[id] = index;

          return prevValueVar;
        }, {})[action.id];
      const dplEvent = eventsArr[indexOfDuplicatedEvent];

      eventsArr.splice(indexOfDuplicatedEvent + 1, 0, createDuplicateEvent(dplEvent));

      return state.events.length < alphabet.length
        ? {
            ...state,
            events: moveCharRight(eventsArr, indexOfDuplicatedEvent + 1),
          }
        : state;
    }

    case 'DASHBOARD_EVENTS_COMPOSE_REMOVE_EVENT':
      return {
        ...state,
        events: moveCharLeft(state.events, action.id).filter((event) => event.id !== action.id),
      };

    case 'DASHBOARD_EVENTS_COMPOSE_SET_GROUP_COLUMN':
      return {
        ...state,
        groupColumn: action.groupColumn,
        options: {
          ...state.options,
          view: getViews(action.groupColumn)[0],
        },
      };

    case 'DASHBOARD_CHANGE_EVENT_VIEW':
      return {
        ...state,
        options: {
          ...state.options,
          view: action.view,
        },
      };

    case 'DASHBOARD_TOGGLE_EVENTS_CALC_TOTAL':
      return {
        ...state,
        options: {
          ...state.options,
          calcTotal: !state.options.calcTotal,
        },
      };

    case 'DASHBOARD_TOGGLE_EVENTS_TREND_LINE':
      return {
        ...state,
        options: {
          ...state.options,
          trendLine: !state.options.trendLine,
        },
      };

    case 'DASHBOARD_TOGGLE_EVENTS_AVERAGE_LINE':
      return {
        ...state,
        options: {
          ...state.options,
          averageLine: !state.options.averageLine,
        },
      };

    case 'DASHBOARD_TOGGLE_EVENTS_MEDIAN_LINE':
      return {
        ...state,
        options: {
          ...state.options,
          medianLine: !state.options.medianLine,
        },
      };

    case 'DASHBOARD_TOGGLE_EVENTS_ADAPTIVE':
      return {
        ...state,
        options: {
          ...state.options,
          adaptive: !state.options.adaptive,
        },
      };

    case 'DASHBOARD_TOGGLE_EVENTS_CUMULATIVE':
      return {
        ...state,
        options: {
          ...state.options,
          cumulative: !state.options.cumulative,
        },
      };

    case 'DASHBOARD_CHANGE_EVENTS_COLUMN_CHART_SORTING':
      return {
        ...state,
        options: {
          ...state.options,
          columnChartSorting: action.sorting,
        },
      };

    case 'DASHBOARD_EVENTS_COMPOSE_SET_EVENT_NAME': {
      const idPredicate = idPredicateBuilder(action.id);

      const predicate = usageEvents[action.name]
        ? (e) => idPredicate(e) || usageEvents[e.name]
        : idPredicate;

      const getAggregationColumn = ({ aggregationType, splitColumn, aggregationColumn }) =>
        (getEventAggregationType(aggregationType, splitColumn) === aggregationTypes.COUNT &&
          aggregationType === aggregationTypes.AVG_TOTAL_SPLIT) ||
        isEmptyOrSpecificField(aggregationColumn)
          ? null
          : aggregationColumn;

      const updatedEvents = updateEvents(state.events, predicate, (event) => {
        const filter = excludeEventSpecificClauses(
          event.filter,
          event.name || '',
          action.name,
          action.eventMetainf
        );
        return {
          ...event,
          name: action.name,
          filter,
          aggregationColumn: getAggregationColumn(event),
          splitColumn: isEmptyOrSpecificField(event.splitColumn) ? null : event.splitColumn,
          aggregationType: getEventAggregationType(event.aggregationType, event.splitColumn),
          othersFilter: filter.clauses?.find(({ id }) => id === event.othersFilter)
            ? event.othersFilter
            : null,
        };
      });

      return {
        ...state,
        events: updatedEvents,
        groupColumn: updateGroupColumn(state.groupColumn, updatedEvents),
      };
    }

    case 'DASHBOARD_EVENTS_COMPOSE_SET_EVENT_AGGREGATION_TYPE':
      return {
        ...state,
        events: updateEventById(state.events, action.id, (event) => ({
          ...event,
          aggregationColumn: null,
          aggregationType: action.aggregationType,
        })),
      };

    case 'DASHBOARD_EVENTS_COMPOSE_SET_EVENT_AGGREGATION_COLUMN':
      return {
        ...state,
        events: updateEventById(state.events, action.id, (event) => ({
          ...event,
          aggregationColumn: action.aggregationColumn,
        })),
      };

    case 'DASHBOARD_EVENTS_COMPOSE_SET_EVENT_SPLIT_COLUMN':
      return {
        ...state,
        events: updateEventById(state.events, action.id, (event) => ({
          ...event,
          splitColumn: action.splitColumn,
        })),
      };

    case 'DASHBOARD_SET_OTHERS_FILTER':
      return {
        ...state,
        events: updateEventById(state.events, action.eventId, (event) => ({
          ...event,
          othersFilter: action.clauseId,
        })),
      };

    case 'DASHBOARD_EXPOSE_EVENT_QUERY_SUCCESS':
      return {
        ...state,
        events: updateEventById(state.events, action.id, (event) => ({
          ...event,
          error: '',
          sql: action.sql,
        })),
      };

    case 'DASHBOARD_SET_EVENT_EXPOSURE_ERROR':
      return {
        ...state,
        events: updateEventById(state.events, action.id, (event) => ({
          ...event,
          sql: '',
          error: action.error,
        })),
      };

    case dashboardEventsFilterActions.ADD_FILTER_CLAUSE:
    case dashboardEventsFilterActions.COPY_FILTER:
    case dashboardEventsFilterActions.REMOVE_FILTER:
    case dashboardEventsFilterActions.SET_FILTER_CLAUSE_FIELD:
    case dashboardEventsFilterActions.SET_FILTER_CLAUSE_VALUE:
    case dashboardEventsFilterActions.SET_CLAUSES_CONCAT_OPERATOR:
    case dashboardEventsFilterActions.SET_FILTER_CLAUSE_OPERATOR:
    case dashboardEventsFilterActions.SET_FILTER_VISIBILITY:
      return {
        ...state,
        events: updateEventById(state.events, action.eventId, (event) => ({
          ...event,
          filter: filterReducer(event.filter, action),
        })),
      };

    case dashboardEventsFilterActions.REMOVE_FILTER_CLAUSE:
      return {
        ...state,
        events: updateEventById(state.events, action.eventId, (event) => ({
          ...event,
          filter: filterReducer(event.filter, action),
          othersFilter: event.othersFilter !== action.id ? event.othersFilter : null,
        })),
      };

    case 'DASHBOARD_ADD_PERIOD':
    case 'DASHBOARD_DELETE_PERIOD':
    case 'DASHBOARD_SET_PERIOD':
    case 'DASHBOARD_SET_SHOW_PERIODS':
      return {
        ...state,
        events: updateEventById(state.events, action.eventId, (event) => ({
          ...event,
          periodsState: periodReducer(event.periodsState, action),
          othersFilter: null,
        })),
      };

    case `DASHBOARD_SET_TEMPLATE_${unitsTypes.EVENTS_TYPE}`: {
      const groupColumn = action.template.queries[0].query.groupBy[0];
      return {
        ...initialEventState,
        events: getEventsFromTemplate(action.template),
        groupColumn,
        idUsingTemplate: action.template.id,
        options: {
          ...state.options,
          view: getView(state.options.view, groupColumn),
        },
      };
    }

    case 'DASHBOARD_SET_EVENT_CSV_COLUMN':
      return {
        ...state,
        csv: {
          ...state.csv,
          ...action.csv,
        },
      };

    case 'DASHBOARD_EVENT_ENTER_FORMULA':
      return {
        ...state,
        options: {
          ...state.options,
          enteredFormula: action.enteredFormula,
        },
      };

    case 'DASHBOARD_EVENT_ENTER_LEGEND_NAME':
      return {
        ...state,
        options: {
          ...state.options,
          legendName: action.name,
        },
      };

    case 'DASHBOARD_EVENT_FORMULA_ONLY':
      return {
        ...state,
        options: {
          ...state.options,
          isFormulaOnly: !state.options.isFormulaOnly,
        },
      };

    default:
      return state;
  }
};

const getEvent = (state, id) => (state.events || []).find((e) => e.id === id) || {};

export default eventsCompose;

export const getIds = (state) => state.events.map((e) => e.id);

export const getGroupColumn = (state) => state.groupColumn;

export const getSql = (state, id) => getEvent(state, id).sql;

export const getError = (state, id) => getEvent(state, id).error;

export const getName = (state, id) => getEvent(state, id).name;

export const getUniqueChar = (state, id) => getEvent(state, id).uniqueChar;

export const getPeriodsState = (state, id) => getEvent(state, id).periodsState;

export const getFilter = (state, id) => getEvent(state, id).filter;

export const getAggregationColumn = (state, id) => getEvent(state, id).aggregationColumn;

export const getAggregationType = (state, id) => getEvent(state, id).aggregationType;

export const getSplitColumn = (state, id) => getEvent(state, id).splitColumn;

export const getEvents = (state) => state.events;

export const getIdUsingTemplate = (state) => state.idUsingTemplate;

export const getCsv = (state) => state.csv;

export const getOthersFilter = (state, id) => getEvent(state, id).othersFilter;

export const getEventView = (state) => state.options.view;

export const getEnteredFormula = (state) => state.options.enteredFormula;

export const getEnteredLegendName = (state) => state.options.legendName;

export const getIsFormulaOnly = (state) => state.options.isFormulaOnly;

export const getEventOptions = (state) => state.options;
